import {
  Celsius,
  TemperatureUnit,
} from '../../../../../system/models/temperatureUnits';
import {
  convertLimitTemperature,
  convertToTempUnits,
} from '../../../util/convert-temp-units';
import { CurrentAttributes } from '../types/current-attributes';
import { MyThermostat } from '../types/my-thermostat';
import {
  admissibleDomainEnds,
  isLegacyCoreThermostat,
} from '../../../../../../common/thermostat-limits';
import { getAutoTargetUpdates } from './auto-target';
import {
  MitsubishiPowerSnapshot,
  getMitsubishiTargetEditBlockReason,
  getMitsubishiOperatingModes,
  getMitsubishiFanSpeeds,
  getMitsubishiAirDirections,
} from './mitsubishi-power-edit';

/** A mode not yet observed can start its draft at the last reported shared target.
 * This is an editor default, never telemetry or an implicit target command.
 */
export function getSharedTargetDefaults(
  baseline: Partial<CurrentAttributes>,
  mode: unknown,
): Partial<CurrentAttributes> {
  const currentKey =
    baseline.operatingMode === 'Heat'
      ? 'heatSetpoint'
      : baseline.operatingMode === 'Cool'
        ? 'coolSetpoint'
        : baseline.operatingMode === 'Auto' &&
            baseline.supportsSingleSetpointAutoMode
          ? 'setpoint'
          : undefined;
  const targetKey =
    mode === 'Heat'
      ? 'heatSetpoint'
      : mode === 'Cool'
        ? 'coolSetpoint'
        : mode === 'Auto' && baseline.supportsSingleSetpointAutoMode
          ? 'setpoint'
          : undefined;
  if (
    !currentKey ||
    !targetKey ||
    Number.isFinite(baseline[targetKey]) ||
    !Number.isFinite(baseline[currentKey])
  )
    return {};
  return { [targetKey]: baseline[currentKey] };
}

export function usesCoreCentidegreeTargets(device?: MyThermostat): boolean {
  return (
    !!device &&
    device.isMitsubishiThermostat !== true &&
    isLegacyCoreThermostat({
      source: { type: device.source?.type ?? undefined },
      deviceId: device.deviceId ?? undefined,
      meta: {
        manufacturer: device.meta?.manufacturer ?? undefined,
        type: device.meta?.type ?? undefined,
      },
      deviceSpec: {
        thermostat: {
          controlContract:
            device.deviceSpec?.thermostat?.controlContract ?? undefined,
        },
      },
    })
  );
}

/** Keep legacy Core's centidegree command inside the exact entered policy.
 * Only repair a rounding crossing; an actually out-of-policy target is not clamped.
 */
function coreTargetWithinPolicy(
  draft: Record<string, unknown>,
  key: 'heatSetpoint' | 'coolSetpoint' | 'setpoint',
  rounded: number,
  units: TemperatureUnit,
) {
  const heat = key !== 'coolSetpoint';
  const cool = key !== 'heatSetpoint';
  const minima = [
    ...(heat ? [draft.minHeatSetpointLimit] : []),
    ...(cool ? [draft.minCoolSetpointLimit] : []),
  ];
  const maxima = [
    ...(heat ? [draft.maxHeatSetpointLimit] : []),
    ...(cool ? [draft.maxCoolSetpointLimit] : []),
  ];
  if ([...minima, ...maxima].some((value) => !Number.isFinite(value)))
    return rounded;
  if (key === 'setpoint') {
    if (Number.isFinite(draft.minSetpoint)) minima.push(draft.minSetpoint);
    if (Number.isFinite(draft.maxSetpoint)) maxima.push(draft.maxSetpoint);
  }
  const lower = Math.max(
    ...minima.map(
      (value) => convertLimitTemperature(value as number, Celsius, units)!,
    ),
  );
  const upper = Math.min(
    ...maxima.map(
      (value) => convertLimitTemperature(value as number, Celsius, units)!,
    ),
  );
  const exact = convertLimitTemperature(draft[key] as number, Celsius, units);
  if (exact === undefined || exact < lower || exact > upper)
    throw new Error('Target is outside the active setpoint limits.');
  if (rounded >= lower && rounded <= upper) return rounded;
  // EMA rounds Celsius targets to integer hundredths. Sending the exact bound
  // would still round outside policy; select the nearest grid point inward.
  const ends = admissibleDomainEnds({
    interval: { lower, upper, lowerInclusive: true, upperInclusive: true },
    step: 0.01,
  });
  const target = rounded < lower ? ends.minimum : ends.maximum;
  if (target === undefined)
    throw new Error(
      'No 0.01 °C Core target exists within the active setpoint limits.',
    );
  return target;
}

/** Build one update from the edited controls, converting target values to source Celsius. */
export function buildThermostatControlEdit(
  baseline: object,
  draft: object,
  snapshot?: MitsubishiPowerSnapshot,
  units: TemperatureUnit = Celsius,
  device?: MyThermostat,
) {
  const after = draft as Record<string, unknown>;
  const before = {
    ...baseline,
    ...(snapshot ? getSharedTargetDefaults(baseline, after.operatingMode) : {}),
  } as Record<string, unknown>;
  const updates: {
    powerMode?: 'On' | 'Off';
    operatingMode?: string;
    fanMode?: string;
    fanSpeed?: string;
    airDirection?: string;
    heatSetpoint?: number;
    coolSetpoint?: number;
    setpoint?: number;
  } = {};
  const singleAuto =
    after.supportsSingleSetpointAutoMode && after.operatingMode === 'Auto';
  const keys = snapshot
    ? (['powerMode', 'operatingMode', 'fanSpeed', 'airDirection'] as const)
    : (['operatingMode', 'fanMode'] as const);
  for (const key of keys) {
    if (after[key] === undefined || after[key] === before[key]) continue;
    Object.assign(updates, { [key]: after[key] });
  }
  const selectedMode = after.operatingMode ?? snapshot?.operatingMode?.value;
  const targetMode =
    snapshot && (after.powerMode ?? snapshot.powerMode?.value) !== 'On'
      ? 'Off'
      : selectedMode;
  // Native shared targets are qualified by the final mode. Retain hidden edits
  // in the draft, but never send a Heat target as a Cool target (or while Off).
  const targetKeys =
    !snapshot || targetMode === 'Auto'
      ? (['heatSetpoint', 'coolSetpoint'] as const)
      : targetMode === 'Heat'
        ? (['heatSetpoint'] as const)
        : targetMode === 'Cool'
          ? (['coolSetpoint'] as const)
          : [];
  if (singleAuto && targetMode === 'Auto') {
    Object.assign(
      updates,
      getAutoTargetUpdates(
        before as Partial<CurrentAttributes>,
        after as Partial<CurrentAttributes>,
        units,
        !!snapshot,
      ),
    );
  } else {
    for (const key of targetKeys) {
      if (after[key] === undefined || after[key] === before[key]) continue;
      updates[key] = snapshot
        ? convertLimitTemperature(after[key] as number, Celsius, units)
        : convertToTempUnits(after[key] as number, Celsius, units, 2);
    }
  }
  if (!snapshot) {
    const core = usesCoreCentidegreeTargets(device);
    if (core)
      for (const key of ['heatSetpoint', 'coolSetpoint', 'setpoint'] as const)
        if (updates[key] !== undefined)
          updates[key] = coreTargetWithinPolicy(
            after,
            key,
            updates[key]!,
            units,
          );
    return { updates, options: {} };
  }
  const hasTargets = ['heatSetpoint', 'coolSetpoint', 'setpoint'].some((key) =>
    Object.prototype.hasOwnProperty.call(updates, key),
  );
  if (
    snapshot.powerMode?.pending ||
    snapshot.operatingMode?.pending ||
    snapshot.fanSpeed?.pending ||
    snapshot.airDirection?.pending
  )
    throw new Error('A thermostat command is still pending.');
  if (
    updates.powerMode !== undefined &&
    (!snapshot.canEditPowerMode || !['On', 'Off'].includes(updates.powerMode))
  )
    throw new Error('Power editing is not available.');
  if (
    updates.operatingMode !== undefined &&
    (!snapshot.canEditOperatingMode ||
      !getMitsubishiOperatingModes(snapshot).includes(updates.operatingMode))
  )
    throw new Error('Operating mode editing is not available.');
  if (
    updates.fanSpeed !== undefined &&
    (!snapshot.canEditFanSpeed ||
      !getMitsubishiFanSpeeds(snapshot).includes(updates.fanSpeed))
  )
    throw new Error('Fan speed editing is not available for this selection.');
  if (
    updates.airDirection !== undefined &&
    (!snapshot.canEditAirDirection ||
      !getMitsubishiAirDirections(snapshot).includes(updates.airDirection))
  )
    throw new Error('Louvre editing is not available for this selection.');
  if (hasTargets) {
    const reason = getMitsubishiTargetEditBlockReason(snapshot, false, after);
    if (reason) throw new Error(reason);
    for (const key of ['heatSetpoint', 'coolSetpoint', 'setpoint'] as const)
      if (updates[key] !== undefined && !Number.isFinite(updates[key]))
        throw new Error('Target must be a valid temperature.');
  }
  return {
    updates,
    options: { thermostatControlContract: 'separate-power-v1' as const },
  };
}
